import React, { useEffect, useRef, useState } from 'react'

import { useComponentVisible } from 'helpers/useComponentVisible'
import { useHistory, useLocation } from 'react-router-dom'
import { useAppSelector } from 'redux-store/hooks'
import { channelSelector } from 'redux-store/channel/channel.selectors'

import { HamburgerMenu } from '../header/components/HamburgerMenu/HamburgerMenu'

import { BottomBarButton } from './components/BottomBarButton/BottomBarButton'
import { MenuSVGIcon } from './assets/MenuSVGIcon'
import { getRoutes } from './utils/route.utils'
import { NATIVE_APP_HELPER } from 'constants/native_app_helper.constants'

import { type AppBottomBarRoute } from './types'

import styles from './MobileAppBottomBar.module.scss'
import { getChannel } from 'api/channel/getChannel'
import useCompanyParam from 'hooks/CompanyParam'

export const MobileAppBottomBar = () => {
  const channel = useAppSelector(channelSelector)
  const history = useHistory()
  const location = useLocation()
  const channelRef = useRef<string | undefined>()
  const currentDomainRef = useRef(window.location.host)
  const companyName = useCompanyParam()
  const [companySubdomain, setCompanySubdomain] = useState<string | undefined>(
    channel?.data?.parentChannelSubdomain || channel?.data?.subdomain
  )

  const routes = getRoutes({
    subdomains: {
      parrentSubDomain: channelRef.current || companySubdomain,
      childSubDomain: companyName
    },
    isError: channel.isError
  })
  const [activeMenu, setActiveMenu] = useState(routes[0].name)

  const { isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false)

  const toggleHamburgerMenu = () => {
    setIsComponentVisible((prev) => !prev)
    setActiveMenu((prev) => (prev === 'Profile' ? '' : 'Profile'))
  }

  const fetchCompanySubdomain = async () => {
    if (!companySubdomain) {
      try {
        //TODO
        // Fetch the company subdomain based on the current hostname
        // I added prby.sportstage.se just for local poweredby testing (to have access to its channel data)
        // We should have a better way to handle this in production maybe in the backend
        const response = await getChannel(
          window.location.hostname === 'prby.sportstage.se'
            ? 'handbollplay.se'
            : window.location.hostname
        )
        setCompanySubdomain(response?.subdomain)
      } catch (error) {
        console.error('Error fetching companySubdomain:', error)
      } finally {
      }
    }
  }

  useEffect(() => {
    fetchCompanySubdomain()
  }, [channel.isLoading, window.location.pathname])

  useEffect(() => {
    if (!channelRef.current && channel?.data) {
      channelRef.current = channel.data.subdomain
    }
  }, [channel.isLoading, window.location.pathname])

  const handleClick = (route: AppBottomBarRoute) => {
    setIsComponentVisible(false)
    setActiveMenu(route.name)
    history.push(route.path)
  }

  useEffect(() => {
    if (
      currentDomainRef.current !==
        NATIVE_APP_HELPER.SOLID_SPORT_APP.GET_DOMAIN &&
      !channelRef.current
    ) {
      setActiveMenu(routes[0].name)
    }
  }, [])

  useEffect(() => {
    for (const route of routes) {
      if (location.pathname.endsWith(route.path) && activeMenu !== 'Profile') {
        setActiveMenu(route.name)
        break
      } else if (activeMenu !== 'Profile') {
        setActiveMenu('')
      }
    }
  }, [location.pathname, isComponentVisible, NATIVE_APP_HELPER.GET_HOST])

  return (
    <div className={styles.MobileAppBottomBarContainer}>
      <div className={styles.MobileAppBottomBarMenu}>
        {/* Route Buttons */}
        {routes.map((route) => (
          <BottomBarButton
            onClick={() => handleClick(route)}
            icon={route.icon}
            key={route.name}
            label={route.name}
            isActive={route.name === activeMenu}
          />
        ))}
        {/* Menu Button */}
        <BottomBarButton
          onClick={toggleHamburgerMenu}
          icon={MenuSVGIcon}
          label={'Profile'}
          isActive={activeMenu === 'Profile'}
        />
        {/* Menu Modal */}
        {isComponentVisible && (
          <HamburgerMenu
            isOpen={isComponentVisible}
            onClose={toggleHamburgerMenu}
          />
        )}
      </div>
    </div>
  )
}
